/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Button } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import AgentAssignSubaccountModal from "./AgentAssignSubaccountModal";
import AgentRevokeSubaccountConfirmModal from "./AgentRevokeSubaccountConfirmModal";
import { Tooltip, IconButton } from "@mui/material";
import SweetAlert2 from "react-sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MdRemoveCircleOutline, MdOutlineContentCopy } from "react-icons/md";
import { AiOutlineFileSearch } from "react-icons/ai";
import { Link } from "react-router-dom";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type RowData = {
  id: string;
  name: string;
};

const AgentSubaccountList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  agentDetail: any;
  assignedSubaccountIds: any;
  triggerReloadList(): void;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  agentDetail,
  assignedSubaccountIds,
  triggerReloadList,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const [isOpenAssignSubaccountModal, setIsOpenAssignSubaccountModal] =
    useState<boolean>(false);
  const [isOpenRevokeSubaccountModal, setIsOpenRevokeSubaccountModal] =
    useState<boolean>(false);
  const [subaccountDetailToRevoke, setSubaccountDetailToRevoke] =
    useState<any>(null);

  const [swalProps, setSwalProps] = useState<any>({});

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const NameRenderer = (params: ICellRendererParams) => (
    <>{params.data.suba_name || params.data.name}</>
  );

  const ActionsRenderer = (params: ICellRendererParams) => (
    <div className="mr-5">
      <Tooltip title="View More Details" arrow placement="top">
        <IconButton
          component={Link}
          to={`/${userRole}/merchants/subaccounts/${params.data.id}`}
          id={"button-detail-" + params.data?.id}
          size="small"
        >
          <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Revoke from agent" arrow placement="top">
        <IconButton
          id={"button-revoke-from-agent-" + params.data?.id}
          onClick={() => handleClickRevokeSubaccount(params.data)}
          size="small"
        >
          <MdRemoveCircleOutline className="h-5 w-5 text-red-500 opacity-90" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "suba_name",
      headerName: "Name",
      sortable: false,
      minWidth: 175,
      flex: 1,
      cellRenderer: NameRenderer,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 140,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const showAddAccountModal = () => {
    setIsOpenAssignSubaccountModal(true);
  };

  const closeAddAccountModal = () => {
    setIsOpenAssignSubaccountModal(false);
  };

  const handleClickRevokeSubaccount = (subaccount: any) => {
    setSubaccountDetailToRevoke(subaccount);
    showRevokeSubaccountModal();
  };

  const showRevokeSubaccountModal = () => {
    setIsOpenRevokeSubaccountModal(true);
  };

  const closeRevokeSubaccountModal = () => {
    setSubaccountDetailToRevoke(null);
    setIsOpenRevokeSubaccountModal(false);
  };

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <div className="px-4 py-8">
      {isOpenAssignSubaccountModal && agentDetail !== null && (
        <AgentAssignSubaccountModal
          setSwalProps={setSwalProps}
          triggerReloadList={triggerReloadList}
          agentDetail={agentDetail}
          isOpen={isOpenAssignSubaccountModal}
          assignedSubaccountIds={assignedSubaccountIds}
          onClose={closeAddAccountModal}
        />
      )}

      {isOpenRevokeSubaccountModal && subaccountDetailToRevoke && (
        <AgentRevokeSubaccountConfirmModal
          setSwalProps={setSwalProps}
          triggerReloadList={triggerReloadList}
          agentDetail={agentDetail}
          subaccountDetailToRevoke={subaccountDetailToRevoke}
          isOpen={isOpenRevokeSubaccountModal}
          onClose={closeRevokeSubaccountModal}
        />
      )}

      <div className="mb-3 flex items-center justify-end">
        <Button
          isDisabled={isLoading}
          onClick={() => showAddAccountModal()}
          className="btn btn-action !px-6"
        >
          <FaPlus />
          &nbsp;Assign Subaccount
        </Button>
      </div>
      <Box>
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </div>
  );
};

export default AgentSubaccountList;
